import type { EmailBlock, EmailHeader, TiptapContent } from '../types'

type TranslateFunction = (key: string) => string

/**
 * Helper to create TipTap JSON content from text
 */
const createTiptapContent = (text: string): TiptapContent => {
  const lines = text.replace(/<br\s*\/?>/gi, '\n').split('\n')
  return {
    type: 'doc',
    content: lines.map((line) => ({
      type: 'paragraph',
      content: line ? [{ type: 'text', text: line }] : []
    }))
  }
}

/**
 * Helper to create TipTap JSON content with H1 heading
 */
const createHeadingContent = (text: string): TiptapContent => ({
  type: 'doc',
  content: [
    {
      type: 'heading',
      attrs: { level: 1 },
      content: text ? [{ type: 'text', text }] : []
    }
  ]
})

export interface EmailTemplate {
  id: string
  name: string
  description: string
  thumbnail?: string
  metadata: {
    from: string
    subject: string
    replyTo: string
    previewText: string
  }
  header: EmailHeader
  blocks: EmailBlock[]
}

/**
 * Template vide
 */
export const createEmptyTemplate = (_t: TranslateFunction): EmailTemplate => ({
  id: 'empty',
  name: '',
  description: '',
  metadata: {
    from: 'contact@heypongo.link',
    subject: '',
    replyTo: '',
    previewText: ''
  },
  header: {
    logo: '',
    showLogo: true,
    align: 'center',
    link: ''
  },
  blocks: [
    {
      id: 'empty-1',
      type: 'image',
      src: '',
      alt: '',
      width: 100,
      align: 'center',
      link: '',
      mask: 'none'
    },
    { id: 'empty-2', type: 'spacer', height: 16 },
    {
      id: 'empty-3',
      type: 'text',
      content: { type: 'doc', content: [{ type: 'paragraph' }] },
      align: 'center',
      color: '000000'
    },
    { id: 'empty-4', type: 'spacer', height: 16 },
    {
      id: 'empty-5',
      type: 'footer',
      logoSrc: '',
      content: createTiptapContent(''),
      align: 'center',
      color: '6b7280'
    }
  ]
})

/**
 * Template Offre Flash — structure haute conversion
 */
export const createFlashOfferTemplate = (): EmailTemplate => ({
  id: 'flash-offer',
  name: 'Offre flash VIP',
  description:
    "Email d'offre exclusive à durée limitée — idéal pour réactiver les clients inactifs ou récompenser les fidèles",
  metadata: {
    from: 'contact@heypongo.link',
    subject: '⚡ Offre exclusive : -20% pendant 48h',
    replyTo: '',
    previewText: 'Cette offre est réservée à nos meilleurs clients. Ne la laissez pas passer.'
  },
  header: {
    logo: '',
    showLogo: true,
    align: 'center',
    link: ''
  },
  blocks: [
    { id: 'flash-1', type: 'spacer', height: 8 },
    {
      id: 'flash-2',
      type: 'image',
      src: '',
      alt: 'Offre exclusive',
      width: 100,
      align: 'center',
      link: '',
      mask: 'none'
    },
    { id: 'flash-3', type: 'spacer', height: 24 },
    {
      id: 'flash-4',
      type: 'text',
      content: createHeadingContent('Une offre réservée à nos VIP'),
      align: 'center',
      color: '1e293b'
    },
    { id: 'flash-5', type: 'spacer', height: 8 },
    {
      id: 'flash-6',
      type: 'text',
      content: createTiptapContent(
        'Parce que vous faites partie de nos clients les plus fidèles, on vous offre -20% sur tout le magasin pendant 48h.\n\nPrésentez simplement cet email en caisse. Offre valable en magasin uniquement.'
      ),
      align: 'center',
      color: '6b7280'
    },
    { id: 'flash-7', type: 'spacer', height: 20 },
    {
      id: 'flash-8',
      type: 'button',
      text: "J'en profite maintenant",
      url: '',
      backgroundColor: '7c3aed',
      textColor: 'ffffff',
      align: 'center'
    },
    { id: 'flash-9', type: 'spacer', height: 24 },
    { id: 'flash-10', type: 'separator', color: 'e2e8f0', height: 1 },
    { id: 'flash-11', type: 'spacer', height: 24 },
    {
      id: 'flash-12',
      type: 'text',
      content: createTiptapContent(
        "⏰ Plus que quelques heures pour en profiter !\nRendez-vous en magasin avant la fin de l'offre."
      ),
      align: 'center',
      color: '475569'
    },
    { id: 'flash-13', type: 'spacer', height: 16 },
    {
      id: 'flash-14',
      type: 'button',
      text: 'Voir les magasins',
      url: '',
      backgroundColor: '1e293b',
      textColor: 'ffffff',
      align: 'center'
    },
    { id: 'flash-15', type: 'spacer', height: 16 },
    { id: 'flash-16', type: 'separator', color: 'e2e8f0', height: 1 },
    {
      id: 'flash-17',
      type: 'footer',
      logoSrc: '',
      content: createTiptapContent(''),
      align: 'center',
      color: '6b7280'
    }
  ]
})

/**
 * Clone un template avec de nouveaux IDs uniques pour les blocs
 */
export const cloneTemplate = (template: EmailTemplate): EmailTemplate => {
  const timestamp = Date.now()
  return {
    ...template,
    id: `${template.id}-${timestamp}`,
    metadata: { ...template.metadata },
    header: { ...template.header },
    blocks: template.blocks.map((block, index) => {
      const clonedBlock = {
        ...block,
        id: `block-${timestamp}-${index}`
      }
      // Deep clone TipTap content for title and text blocks
      if ('content' in clonedBlock && typeof clonedBlock.content === 'object') {
        clonedBlock.content = JSON.parse(JSON.stringify(clonedBlock.content))
      }
      return clonedBlock
    })
  }
}
